// src/components/UserEdit.js

import React, { useEffect, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { isAuthenticated, getUser } from '../utils/auth';
import { COUNTRIES } from '../utils/countries';
import Swal from 'sweetalert2';

const UserDetails = () => {
  const { id: userId } = useParams();  // Use useParams to get the route parameter
  const [User, setUser] = useState({
    name: '',
    email: '',
    ic_no: '',
    role: '',
    company: '',
    department: '',
    designation: '',
    employeeNo: '',
    contactNo: '',
    emergencyContactNo: '',
    relationship: '',
    nationality: '',
    status: 'active',
    assignedTrainings: ''
  });
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const loggedInUser = getUser();
  console.log ("loggedinuser ", loggedInUser);

  // Fetch companies on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/companies/get_companies.php`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setCompanies(data);
        } else {
          throw new Error("Invalid companies data format");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch departments when the company is selected
  useEffect(() => {
    if (User.company) {
      const fetchDepartments = async () => {
        setLoadingDepartments(true);
        try {
          const response = await fetch(`${API_BASE_URL}/departments/get_departments.php?company_id=${User.company}`);
          const data = await response.json();
          if (Array.isArray(data)) {
            setDepartments(data);
          } else {
            throw new Error("Invalid departments data format");
          }
        } catch (error) {
          setError(error.message);
        } finally {
          setLoadingDepartments(false);
        }
      };
      fetchDepartments();
    } else {
      setDepartments([]);  // Clear departments if no company is selected
    }
  }, [User.company]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/users/user_details.php?id=${userId}`,{
          withCredentials: true
        });
        setUser(response.data);
        console.log("data",response.data);
      } catch (error) {
        console.error('There was an error fetching the user details!', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserDetails();
  }, [userId]);

  // Fetch roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/users/get_roles.php`, { credentials: 'include' });
        const data = await response.json();
        if (Array.isArray(data)) {
          setRoles(data);
        } else {
          throw new Error("Invalid roles data format");
        }
      } catch (error) {
        setError(error.message);
       }
    };
    fetchRoles();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prevUser) => ({
      ...prevUser,
      [name]: value
    }));
  };

  const handleSave = () => {
    console.log(User);
    const formData = new FormData();
    Object.keys(User).forEach(key => {
      formData.append(key, User[key]);  
    });
    formData.append('id',userId);
    formData.append('isUpdate',true);
  
    console.log("formdata",formData);

    fetch(`${API_BASE_URL}/users/save_user.php`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
    .then(response => {
      const contentType = response.headers.get('content-type');
  
      // Log the status and response text for debugging
      console.log('Response Status:', response.status);
      
      // Attempt to read the response as text first
      return response.text().then((text) => {
        console.log('Raw Response:', text); // Log the raw response text
        
        if (!response.ok) {
          // If response is not ok, throw an error with the response text
          throw new Error(`Failed with status ${response.status}: ${text}`);
        }
  
        // If response is JSON, parse it
        if (contentType && contentType.includes('application/json')) {
          return JSON.parse(text); // Parse JSON manually
        } else {
          // Return the raw text if it's not JSON
          return text;
        }
      });
    })
    .then(data => {
      console.log("data",data);
      // Check if the response is JSON or plain text
      if (typeof data === 'string') {
        console.log('Non-JSON response:', data);
        Swal.fire({ icon: 'error', text: `Received non-JSON response: ${data}` });
      } else if (data.success) {
        navigate('/users');
      } else {
        console.error('PHP Error:', data.error); // Log PHP error to the console
        Swal.fire({ icon: 'error', text: `Failed to save user: ${data.error}` });
      }
    })
    .catch(error => {
      console.error('Error saving user:', error);
      Swal.fire({ icon: 'error', text: `Error: ${error.message}` });
    });
  };

  const handleCancel = () => {
    // Handle cancel functionality
    console.log('Operation canceled');
    navigate('/users');
  };

  if (loadingCompanies) {
    return <div>Loading Companies...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }
  
  return (
    <div className="p-2">
      <h2>User Details</h2>
      <Form>
        <Form.Group controlId="name">
          <Form.Label>Name</Form.Label>
          <Form.Control type="text" name="name" value={User.name} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="email">
          <Form.Label>Email</Form.Label>
          <Form.Control type="email" name="email" value={User.email} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="nationality">
          <Form.Label>Nationality</Form.Label>
          <Form.Control as="select" name="nationality" value={User.nationality || ''} onChange={handleChange}>
            <option value="">Select a nationality</option>
            {COUNTRIES.map((country) => (
              <option key={country} value={country}>{country}</option>
            ))}
          </Form.Control>
        </Form.Group>
        <Form.Group controlId="ic_no">
          <Form.Label>IC/Passport No.</Form.Label>
          <Form.Control type="text" name="ic_no" value={User.ic_no} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="role">
          <Form.Label>Role</Form.Label>
          <Form.Control as="select" name="role" value={User.role} onChange={handleChange} >
          <option value="" hidden>Select a role</option>
          {roles
            // Filter roles based on the logged-in user's role
            .filter((role) => {
              if (loggedInUser.role === 'trainingadmin') {
                // Only allow 'trainingadmin' and 'trainer' for training admins
                return role.name === 'trainingadmin' || role.name === 'trainer';
              }
              else if (loggedInUser.role === 'masteradmin') {
                return role.name !== 'admin';
              }
              else if (loggedInUser.role === 'companyadmin') {
                return role.name === 'companyadmin' || role.name === 'departmentadmin' || role.name === 'employee';
              }
              else if (loggedInUser.role === 'departmentadmin') {
                return role.name === 'departmentadmin' || role.name === 'employee';
              }
              // For other users, show all roles (you can customize this as needed)
              return true;
            })
            .map((role) => (
              <option key={role.name} value={role.name}>
                {role.label}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        {/* Company Select — a vendor-side role (trainingadmin/trainer) only ever
            sees external (vendor) companies here; every other role only sees
            internal ones. One field, contextually filtered, so a user can
            never end up tied to both at once. */}
        <Form.Group controlId="company">
          <Form.Label>{(User.role === 'trainingadmin' || User.role === 'trainer') ? 'Training Vendor' : 'Company/Subsidiary'}</Form.Label>
          {['companyadmin', 'departmentadmin', 'trainingadmin'].includes(loggedInUser.role) ? (
            <Form.Control
              as="select"
              name="company"
              value={loggedInUser.company_id}
              disabled
            >
              <option value={loggedInUser.company_id}>
            {companies.find(company => company.id === loggedInUser.company_id)?.name}
           </option>
            </Form.Control>
          ) : (
            <Form.Control
              as="select"
              name="company"
              value={User.company || ''}
              onChange={handleChange}
            >
              <option value="">Select a company</option>
              {companies
                .filter((company) => {
                  const wantsExternal = User.role === 'trainingadmin' || User.role === 'trainer';
                  return wantsExternal ? company.company_type === 'external' : company.company_type === 'internal';
                })
                .map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Form.Control>
          )}
        </Form.Group>



         {/* Department Select */}
        <Form.Group controlId="department">
          <Form.Label>Department</Form.Label>
          {['employee', 'departmentadmin'].includes(loggedInUser.role) ? (
             <Form.Control 
             as="select" 
             name="department" 
             value={loggedInUser.department_id} 
             disabled
           >
             <option value={loggedInUser.department_id}>
           {departments.find(department => department.id === loggedInUser.department_id)?.name}
          </option>
           </Form.Control>
         ) : (
          <Form.Control as="select" name="department" value={User.department} onChange={handleChange} disabled={!User.company}>
            <option value="">Select a department</option>
            {loadingDepartments ? (
              <option>Loading departments...</option>
            ) : (
              departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))
            )}
          </Form.Control>
          )}
        </Form.Group>
        <Form.Group controlId="designation">
          <Form.Label>Designation</Form.Label>
          <Form.Control type="text" name="designation" value={User.designation} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="EmployeeNo">
          <Form.Label>Employee No.</Form.Label>
          <Form.Control type="text" name="employeeNo" value={User.employeeNo} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="contactNo">
          <Form.Label>Contact No.</Form.Label>
          <Form.Control type="text" name="contactNo" value={User.contactNo} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="emergencyContactNo">
          <Form.Label>Emergency Contact No.</Form.Label>
          <Form.Control type="text" name="emergencyContactNo" value={User.emergencyContactNo} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="relationship">
          <Form.Label>Relationship</Form.Label>
          <Form.Control type="text" name="relationship" value={User.relationship} onChange={handleChange} />
        </Form.Group>
        {User.status !== 'pending' && (
          <Form.Group controlId="status">
            <Form.Label>Status</Form.Label>
            <Form.Control as="select" name="status" value={User.status} onChange={handleChange}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Control>
          </Form.Group>
        )}
        <div className="pt-3">
        <Button variant="primary" className="mr-2" onClick={handleSave}>Save</Button>
        <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </div>
      </Form>
    </div>
  );
};

export default UserDetails;
